import { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import "./CustomSelect.css";
import { IoMdArrowDropdown } from "react-icons/io";

const CustomSelect = ({
  options = [],
  value,
  onChange,
  placeholder,
  className = "",
  renderOption,
  formatLabel,
}) => {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const selectRef = useRef(null);
  const triggerRef = useRef(null);
  const dropdownRef = useRef(null);
  const [dropdownStyle, setDropdownStyle] = useState({});

  useEffect(() => {
    const handleClickOutside = (event) => {
      const clickedTrigger =
        selectRef.current && selectRef.current.contains(event.target);
      const clickedDropdown =
        dropdownRef.current && dropdownRef.current.contains(event.target);

      if (!clickedTrigger && !clickedDropdown) {
        setOpen(false);
        setSearch("");
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const getLabel = (option) =>
    typeof option === "string" ? option : option?.label || "";

  const filteredOptions = useMemo(() => {
    return options.filter((option) =>
      getLabel(option).toLowerCase().includes(search.toLowerCase())
    );
  }, [options, search]);

  const selectedLabel =
    typeof value === "string"
      ? value
      : value?.label || "";

  useLayoutEffect(() => {
    if (!open || !triggerRef.current) return;

    const updateDropdownPosition = () => {
      if (!triggerRef.current) return;

      const rect = triggerRef.current.getBoundingClientRect();
      const viewportPadding = 8;
      const preferredMaxHeight = 220;
      const gap = 6;
      const spaceBelow = window.innerHeight - rect.bottom - viewportPadding;
      const spaceAbove = rect.top - viewportPadding;
      const openAbove =
        spaceBelow < 180 && spaceAbove > spaceBelow;
      const availableSpace = openAbove ? spaceAbove : spaceBelow;

      setDropdownStyle({
        position: "fixed",
        left: rect.left,
        top: openAbove ? "auto" : rect.bottom + gap,
        bottom: openAbove ? window.innerHeight - rect.top + gap : "auto",
        width: rect.width,
        maxHeight: Math.max(
          120,
          Math.min(preferredMaxHeight, availableSpace - gap),
        ),
        zIndex: 9999,
      });
    };

    updateDropdownPosition();
    window.addEventListener("resize", updateDropdownPosition);
    window.addEventListener("scroll", updateDropdownPosition, true);

    return () => {
      window.removeEventListener("resize", updateDropdownPosition);
      window.removeEventListener("scroll", updateDropdownPosition, true);
    };
  }, [open]);

  return (
    <div className={`custom-select-wrapper ${className}`} ref={selectRef}>
      
    
      <div
        ref={triggerRef}
        className={`custom-select-box ${open ? "open" : ""}`}
        onClick={() => setOpen((prev) => !prev)}
      >
        <span className={selectedLabel ? "selected-value" : "placeholder"}>
          {selectedLabel ? (formatLabel ? formatLabel(selectedLabel) : selectedLabel) : placeholder}
        </span>

        <span className={`arrow ${open ? "rotate" : ""}`}>
          <IoMdArrowDropdown size={20} />
        </span>
      </div>

     
      {open &&
        createPortal(
          <div
            ref={dropdownRef}
            className="custom-select-options"
            style={dropdownStyle}
          >
            <div className="select-search-wrapper">
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search..."
                className="select-search-input"
                onClick={(e) => e.stopPropagation()}
              />
            </div>

            {filteredOptions.length > 0 ? (
              filteredOptions.map((option) => {
                const label = getLabel(option);

                const isActive =
                  selectedLabel === label;

                return (
                  <div
                    key={typeof option === "string" ? option : option.value}
                    className={`custom-option ${isActive ? "active" : ""}`}
                    onClick={() => {
                      onChange(option);
                      setOpen(false);
                      setSearch("");
                    }}
                  >
                    {renderOption
                      ? renderOption(option)
                      : (formatLabel ? formatLabel(label) : label)}
                  </div>
                );
              })
            ) : (
              <div className="no-options">No results found</div>
            )}
          </div>,
          document.body,
        )}
    </div>
  );
};

export default CustomSelect;
